<template>
  <div class="order-refund-modal">
    <CustomModal :show="show"
                 @close="onClose">
      <template #header>
        <h1 class="custom-modal__heading">
          Request to cancel and refund
        </h1>
      </template>

      <div class="order-refund-modal__body">
        <template v-if="loading">
          <div class="order-refund-modal__loading-container">
            <div class="order-refund-modal__loading-content">
              <h5>Loading</h5>
              <font-awesome-icon icon="spinner"
                                 class="icon"
                                 size="lg"
                                 spin>
              </font-awesome-icon>
            </div>
          </div>
        </template>

        <template v-else>
          <template v-if="orderHeader && orderHeader.orderLines && orderHeader.orderLines.length">
            <ul class="order-refund-modal__order-line-lists">
              <li v-for="(item, index) in orderHeader.orderLines"
                  :key="index"
                  class="order-refund-modal__order-line">
                <div class="order-refund-modal__summary">
                  <div class="order-refund-modal__name">
                    {{ item.productName }} ({{ item.refundQuantity }}/{{ item.quantity }})
                  </div>

                  <div class="order-refund-modal__edit"
                       @click="onRefundShow(item)">
                    <template v-if="item.refundRequested">
                      View refund
                    </template>
                    <template v-else>
                      Request to cancel and refund
                    </template>
                  </div>
                </div>

                <div v-if="item.showDetails"
                     class="order-refund-modal__form">
                  <b-form-group v-if="!item.loaded"
                                class="order-refund-modal__form-loading">
                    <h6>Loading refund details...</h6>
                    <font-awesome-icon icon="spinner"
                                       class="icon"
                                       size="lg"
                                       spin></font-awesome-icon>
                  </b-form-group>
                  <template v-else>
                    <b-form-group>
                      <template v-if="item.isEventProduct">
                        <div class="order-refund-modal__delegate">
                          <div class="order-refund-modal__delegate-name"></div>
                          <div class="order-refund-modal__delegate-action">
                            <b-form-checkbox v-model="selectAllDelegates"
                                             aria-describedby="delegates"
                                             aria-controls="delegates"
                                             :disabled="item.refundRequested"
                                             :value="true"
                                             :unchecked-value="false">
                              Select all
                            </b-form-checkbox>
                          </div>
                        </div>
                        <div v-for="(rd, idx) in item.refundDelegates"
                             :key="idx"
                             class="order-refund-modal__delegate">
                          <div class="order-refund-modal__delegate-name">
                            Delegate {{ idx + 1 }} - {{ rd.delegateName }}
                          </div>
                          <div class="order-refund-modal__delegate-action">
                            <b-form-checkbox :id="`delegate-${idx}`"
                                             v-model="rd.requestRefund"
                                             :name="`delegate-${idx}`"
                                             :disabled="item.refundRequested"
                                             :value="true"
                                             :unchecked-value="false">
                              Refund
                            </b-form-checkbox>
                          </div>
                        </div>
                        <span v-if="item.delegatesInvalid"
                              class="validation-error">Please select at least one delegate</span>
                      </template>
                      <template v-else>
                        <b-input v-if="item.refundRequested"
                                 v-model="item.refundQuantity"
                                 :disabled="item.refundRequested">
                        </b-input>
                        <b-select v-else
                                  v-model="item.refundQuantity">
                          <option v-for="(qty, i) in item.refundQuantities"
                                  :key="`${i}:${qty.value}`"
                                  :value="qty.value"
                                  :disabled="item.refundRequested">
                            {{ qty.text }}
                          </option>
                        </b-select>
                        <span v-if="item.quantityInvalid"
                              class="validation-error">Please select a refund quantity</span>
                      </template>
                    </b-form-group>
                    <b-form-group>
                      <b-input v-if="item.refundRequested"
                               v-model="item.refundReason"
                               :disabled="item.refundRequested">
                      </b-input>
                      <b-select v-else
                                v-model="item.refundReasonId">
                        <option v-for="reason in orderHeader.refundReasons"
                                :key="reason.value"
                                :value="reason.value"
                                :disabled="item.refundRequested">
                          {{ reason.text }}
                        </option>
                      </b-select>
                      <span v-if="item.reasonInvalid"
                            class="validation-error">Please select a refund reason</span>
                    </b-form-group>

                    <b-form-group>
                      <b-form-textarea v-model="item.refundDetails"
                                       placeholder="Notes..."
                                       :disabled="item.refundRequested"
                                       rows="5"
                                       max-rows="8"></b-form-textarea>
                    </b-form-group>

                    <b-form-group>
                      <template v-if="!item.refundRequested">
                        <button v-if="!item.refundRequested"
                                class="btn btn-accent-8 btn-sm btn-rounded"
                                @click="onRefundAccept(item)">
                          Accept
                        </button>
                      </template>
                      <template v-else>
                        <button class="btn btn-accent-8 btn-sm btn-rounded"
                                @click="onRefundClose(item)">
                          Close
                        </button>

                        <!-- eslint-disable-next-line max-len -->
                        <button class="btn btn-accent-3 btn-sm btn-rounded order-refund-modal__cancelItem"
                                :disabled="item.isRefundComplete"
                                @click="onRefundCancel(item)">
                          Cancel refund
                        </button>
                      </template>
                    </b-form-group>
                  </template>
                </div>
              </li>
            </ul>
          </template>
          <template v-else>
            <p>No products found!</p>
          </template>

          <div class="order-refund-modal__action-buttons">
            <button class="btn"
                    :disabled="loading || submitting"
                    @click="onCancel">
              <template v-if="cancelling">
                <span class="visually-hidden">
                  Cancelling, please wait...
                </span>
                <font-awesome-icon icon="spinner"
                                   spin></font-awesome-icon>
              </template>

              <template v-else>
                Cancel
              </template>
            </button>

            <button class="btn btn-accent-1"
                    :disabled="submitDisabled"
                    @click="onSubmit">
              <template v-if="submitting">
                <span class="visually-hidden">
                  Submitting, please wait...
                </span>
                <font-awesome-icon icon="spinner"
                                   spin></font-awesome-icon>
              </template>

              <template v-else>
                Submit
              </template>
            </button>
          </div>
        </template>
      </div>
    </CustomModal>
    <ErrorModal :show="errorModalShow"
                :heading="errorModalHeader"
                :title="errorModalTitle"
                :error-message="errorModalMessage"
                @close="onErrorModalClosed">
    </ErrorModal>
  </div>
</template>

<script>
import { getOrderHeader, submitRefund, getOrderLineDelegates } from "~/Scripts/resources/ordersResource";
import CustomModal from "~/Components/CustomModal.vue";
import ErrorModal from "~/Components/Common/ErrorModal.vue";

export default {
  name: "OrderRefundsModal",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    getOrderHeader,
    CustomModal,
    ErrorModal
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: String,
      default: null
    },
    errorModalHeader: {
      type: String,
      default: "Refund Issue"
    }
  },
  data () {
    return {
      orderHeader: null,
      cancelling: false,
      submitting: false,
      loading: false,
      selectAllDelegates: false,
      errorModalShow: false,
      errorModalTitle: null,
      errorModalMessage: null
    };
  },
  computed: {
    submitDisabled () {
      // Test something is ready to submit
      const readyToSubmit = this.orderHeader?.orderLines.some(
        ol => ol.refundRequested && !ol.isRefundComplete
      ) ?? false;

      return this.loading || this.cancelling || !readyToSubmit;
    }
  },
  watch: {
    show: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          this.cancelling = false;
          this.submitting = false;
          this.loading = true;
          this.orderHeader = null;

          getOrderHeader( this.orderId )
            .then( result => {
              if ( result && result.data ) {
                this.orderHeader = result.data;
              }
            } )
            .catch( error => {
              console.log( "an error occured when loading modal data" );
              throw error;
            } )
            .finally( () => {
              this.loading = false;
            } );
        }
      }
    },
    selectAllDelegates: {
      handler ( val ) {
        this.orderHeader.orderLines = this.orderHeader.orderLines.map( o => ( {
          ...o,
          refundDelegates: o.refundDelegates?.map( d => ( {
            ...d,
            requestRefund: val
          } ) )
        } ) );
      }
    }
  },
  methods: {
    onSubmit () {
      if ( !this.submitting ) {
        this.submitting = true;

        submitRefund( this.orderHeader )
          .then( result => {
            if ( result && result.data ) {
              this.$emit( "refreshPage" );
            }
          } )
          .catch( error => {
            console.log( "error saving the refund request" );
            throw error;
          } )
          .finally( () => {
            this.submitting = false;
          } );
      }
    },
    onCancel () {
      if ( !this.cancelling && !this.submitting ) {
        this.cancelling = true;
        this.$emit( "close" );
        this.cancelling = false;
        this.orderHeader = null;
      }
    },
    onClose () {
      this.orderHeader = null;
      this.$emit( "close" );
    },
    onRefundShow ( item ) {
      if ( item.hasRefundError ) {
        this.errorModalTitle = item.refundErrorModalTitle;
        this.errorModalMessage = item.refundErrorModalMessage;
        this.show = false;
        this.errorModalShow = true;
      } else {
        item.showDetails = true;
        this.$set( item, "loaded", false );

        if ( !item.refundDelegates ) {
          getOrderLineDelegates( item.lineId )
            .then( result => {
              item.refundDelegates = result.data || [];
            } )
            .catch( error => {
              item.refundDelegates = [];
              console.log( "error saving the refund request" );
              throw error;
            } )
            .finally( () => {
              item.reasonInvalid = false;
              this.$set( item, "loaded", true );
            } );
        } else {
          item.reasonInvalid = false;
          this.$set( item, "loaded", true );
        }
      }
    },
    onRefundAccept ( item ) {
      if ( item.isEventProduct ) {
        item.delegatesInvalid = item.refundDelegates.every( rd => !rd.requestRefund );
        item.quantityInvalid = false;
      } else {
        item.quantityInvalid = !item.refundQuantity || item.refundQuantity === "0";
        item.delegatesInvalid = false;
      }
      item.reasonInvalid = !item.refundReasonId;

      const isValid = !item.delegatesInvalid && !item.quantityInvalid && !item.reasonInvalid;

      if ( isValid ) {
        item.showDetails = false;
        item.refundRequested = true;
        item.refundReason = this.orderHeader.refundReasons.find(
          r => r.value === item.refundReasonId
        ).text;
      }
    },
    onRefundClose ( item ) {
      item.showDetails = false;
    },
    onRefundCancel ( item ) {
      if ( !item.isRefundComplete ) {
        item.refundRequested = false;
        item.showDetails = false;
        item.refundReasonId = null;
        item.refundReason = null;
        item.refundDetails = "";
      }
    },
    onErrorModalClosed () {
      this.errorModalShow = false;
      this.show = true;
    }
  }
};
</script>

<style lang="less" scoped>
.order-refund-modal {
  &__heading {
    color: #000;
    font-weight: 700;
    margin-bottom: 15px;
  }

  &__text {
    font-size: 14px;
  }

  &__summary {
    display: flex;
    justify-content: space-between;
    align-items: stretch;

    @media (max-width: 991px) {
      flex-direction: column;
    }
  }

  &__name,
  &__edit,
  &__delegate-name,
  &__delegate-action {
    padding: 10px 15px;
    flex-grow: 1;
    border: 0;
  }

  &__name {
    font-weight: 700;
    flex-basis: 70%;
  }

  &__edit {
    flex-basis: 30%;
    background: #e3f3ea;
    border-left: 2px solid #80bdbf;
    border-right: 2px solid #80bdbf;
    transition: background-color 0.2s;
    font-size: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    &:hover {
      background: darken(#e3f3ea, 10%);
    }

    &:last-child {
      border-right: 0;
      border-radius: 0 5px 0 0;
    }

    @media (max-width: 991px) {
      border-left: 0;
      border-right: 0;
      border-top: 2px solid #80bdbf;
      border-bottom: 2px solid #80bdbf;
      text-align: left;

      &:last-child {
        border-bottom: 0;
        border-radius: 0 0 5px 5px;
      }
    }
  }

  &__delegate {
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    border: 2px solid #80bdbf;

    + .order-refund-modal__delegate {
      border-top: 0;

      .order-refund-modal__delegate-name {
        background: #fff;
      }

      .order-refund-modal__delegate-action {
        border-left: 2px solid #80bdbf;

        &:hover {
          background: darken(#e3f3ea, 10%);
        }

        .custom-checkbox {
          width: 83px;
        }
      }
    }

    @media (max-width: 991px) {
      flex-direction: column;
    }
  }

  &__delegate-name {
    text-align: center;
    flex-basis: 70%;
    background: #e3f3ea;
  }

  &__delegate-action {
    flex-basis: 30%;
    background: #e3f3ea;
    transition: background-color 0.2s;
    font-size: 14px;
    display: flex;
    justify-content: center;
  }

  &__order-line-lists {
    list-style: none;
    padding: 0;
    margin: 20px 0 40px 0;

    .btn {
      margin-right: 15px;
    }
  }

  &__order-line {
    border: 2px solid #80bdbf;
    border-radius: 5px;
    margin-bottom: 10px;
  }

  &__form {
    padding: 30px 10px 0 10px;

    &-loading {
      text-align: center;
    }
  }

  &__actions {
    display: flex;
    justify-content: space-between;
  }

  &__action-buttons {
    float: right;
  }

  &__cancelItem {
    background-color: #dc3545;
    &:hover {
      background: darken(#dc3545, 10%);
    }
  }

  &__remove {
    color: #0c499c;
    padding: 0;
    border: 0;
    background: none;
    font-size: 14px;

    &:hover {
      color: darken(#0c499c, 15%);
      text-decoration: underline;
    }
  }

  &--invalid {
    border-color: red;
  }
}

/deep/ .vs__dropdown-toggle {
  padding: 1px 5px 5px 5px;
}

/deep/ .vs__search {
  padding-top: 4px;
  padding-bottom: 4px;
}

/deep/ .v-select {
  font-size: 14px;

  ::placeholder {
    color: #7f7f7f;
    opacity: 1;
  }
}
</style>
